import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  auditActionLabel,
  codeLabel,
  dir,
  t as translate,
  type Locale,
  type MessageKey,
} from '@syrova/i18n';

interface I18n {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** stored code → label (unknown codes show as themselves) */
  code: (ns: string, value: string | null | undefined) => string;
  /** audit action code → label */
  action: (code: string) => string;
}

const Ctx = createContext<I18n | null>(null);
const STORAGE_KEY = 'syrova.locale';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === 'en' ? 'en' : 'ar';
    } catch {
      return 'ar';
    }
  });
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir(locale);
  }, [locale]);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* private mode etc. */
    }
  }, []);
  const value = useMemo<I18n>(
    () => ({
      locale,
      setLocale,
      t: (k, v) => translate(locale, k, v),
      code: (ns, value) => codeLabel(locale, ns, value),
      action: (c) => auditActionLabel(locale, c),
    }),
    [locale, setLocale],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}
